import { Auth } from '@cadstrom/cadstrom-shared';
import AddIcon from '@mui/icons-material/Add';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import FormControl from '@mui/material/FormControl';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import { DataGridPremium } from '@mui/x-data-grid-premium';
import { useMutation, useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { Suspense, useMemo, useState } from 'react';

import { MemberRemoveButton } from '#screens/members/member-remove-button.js';
import { MemberRoleCell } from '#screens/members/member-role-cell.js';
import { useTRPC } from '#src/trpc.js';
import { useDataElTagGenerator } from '#src/utils/use-data-el-tag-generator.js';
import { useAllWizardUsers } from '../use-all-wizard-users.js';

import type { GridColDef } from '@mui/x-data-grid-premium';

export const Route = createFileRoute('/_authorization-gate/wizard/admin/members/')({
	staticData: {
		authorize: (ability) => ability.can('manage', 'membership'),
	},
	head: () => ({ meta: [{ title: 'Members • Wizard Admin • Cadstrom' }] }),
	component: WizardAdminMembersPage,
});

type MemberRow = {
	id: string;
	memberId: string;
	groupId: string;
	username: string;
	displayName: string;
	orgName: string;
	role: Auth.MembershipRole;
};

const WIZARD_ROLE_OPTIONS: Auth.MembershipRole[] = [
	Auth.MembershipRole.Admin,
	Auth.MembershipRole.Member,
	Auth.MembershipRole.Trainee,
];

function WizardAdminMembersPage() {
	const el = useDataElTagGenerator(WizardAdminMembersPage);
	const trpc = useTRPC();
	const queryClient = useQueryClient();

	const [selectedOrgId, setSelectedOrgId] = useState<string>('');
	const [showAddDialog, setShowAddDialog] = useState(false);

	const {
		data: { memberships },
	} = useSuspenseQuery(trpc.membership.getMany.queryOptions({}));

	const organizations = useMemo(() => {
		const orgMap = new Map<string, { id: string; name: string; displayName: string }>();
		for (const m of memberships) {
			if (!orgMap.has(m.group.id)) {
				orgMap.set(m.group.id, {
					id: m.group.id,
					name: m.group.organizationName,
					displayName: m.group.displayName ?? m.group.organizationName,
				});
			}
		}
		return Array.from(orgMap.values()).sort((a, b) => a.displayName.localeCompare(b.displayName));
	}, [memberships]);

	const effectiveOrgId = (selectedOrgId || organizations[0]?.id) ?? '';

	const filteredMemberships = useMemo(
		() => (effectiveOrgId ? memberships.filter((m) => m.group.id === effectiveOrgId) : memberships),
		[memberships, effectiveOrgId],
	);

	const rows = useMemo<MemberRow[]>(
		() =>
			filteredMemberships.map((m) => ({
				id: m.id,
				memberId: m.memberId,
				groupId: m.groupId,
				username: m.member.username,
				displayName: m.member.displayName ?? m.member.username,
				orgName: m.group.displayName ?? m.group.organizationName,
				role: m.role,
			})),
		[filteredMemberships],
	);

	const adminCount = rows.filter((r) => r.role === Auth.MembershipRole.Admin).length;

	const invalidateMemberships = () => {
		void queryClient.invalidateQueries({ queryKey: trpc.membership.getMany.queryKey() });
	};

	const columns = useMemo<GridColDef<MemberRow>[]>(
		() => [
			{ field: 'displayName', headerName: 'Name', flex: 1, minWidth: 150 },
			{ field: 'username', headerName: 'Username', flex: 1, minWidth: 120 },
			{
				field: 'role',
				headerName: 'Role',
				width: 160,
				sortable: false,
				renderCell: ({ row }) => (
					<MemberRoleCell
						memberId={row.memberId}
						groupId={row.groupId}
						role={row.role}
						memberName={row.displayName}
						availableRoles={WIZARD_ROLE_OPTIONS}
						isLastAdmin={row.role === Auth.MembershipRole.Admin && adminCount <= 1}
						promoteTraineeFlow
					/>
				),
			},
			{
				field: 'actions',
				headerName: '',
				width: 60,
				sortable: false,
				filterable: false,
				renderCell: ({ row }) => (
					<MemberRemoveButton
						memberId={row.memberId}
						groupId={row.groupId}
						memberName={row.displayName}
						orgName={row.orgName}
					/>
				),
			},
		],
		[adminCount],
	);

	return (
		<>
			<div {...el()} className='flex min-h-0 grow basis-0 flex-col gap-1.5 p-2'>
				<div className='flex items-center justify-between gap-4'>
					<div className='flex items-center gap-3'>
						<h2 className='ml-1 text-lg font-semibold'>Members</h2>

						{organizations.length > 1 && (
							<FormControl size='small'>
								<Select value={effectiveOrgId} onChange={(e) => setSelectedOrgId(e.target.value)} displayEmpty>
									{organizations.map((org) => (
										<MenuItem key={org.id} value={org.id}>
											{org.displayName}
										</MenuItem>
									))}
								</Select>
							</FormControl>
						)}

						{organizations.length === 1 && (
							<span className='text-sm text-gray-11'>{organizations[0]?.displayName}</span>
						)}
					</div>

					<Button startIcon={<AddIcon />} onClick={() => setShowAddDialog(true)}>
						<span>Add member</span>
					</Button>
				</div>

				<DataGridPremium
					columns={columns}
					rows={rows}
					initialState={{ sorting: { sortModel: [{ field: 'displayName', sort: 'asc' }] } }}
				/>
			</div>

			<AddMemberDialog
				open={showAddDialog}
				orgId={effectiveOrgId}
				onClose={() => setShowAddDialog(false)}
				onSuccess={invalidateMemberships}
			/>
		</>
	);
}

type AddMemberDialogProps = {
	open: boolean;
	orgId: string;
	onClose: () => void;
	onSuccess: () => void;
};

function AddMemberDialog({ open, orgId, onClose, onSuccess }: AddMemberDialogProps) {
	const trpc = useTRPC();
	const [selectedUserId, setSelectedUserId] = useState('');
	const [selectedRole, setSelectedRole] = useState<Auth.MembershipRole>(Auth.MembershipRole.Member);

	const { mutate: addMembership, isPending } = useMutation(
		trpc.membership.add.mutationOptions({
			onSuccess: () => {
				onClose();
				setSelectedUserId('');
				setSelectedRole(Auth.MembershipRole.Member);
				onSuccess();
			},
		}),
	);

	const handleSubmit = () => {
		if (!selectedUserId || !orgId) {
			return;
		}
		addMembership({ memberId: selectedUserId, groupId: orgId, role: selectedRole });
	};

	return (
		<Dialog open={open} onClose={onClose} fullWidth maxWidth='sm'>
			<DialogTitle>Add member</DialogTitle>
			<DialogContent className='flex flex-col gap-4 pt-2'>
				{/* Only fetch the user list once the dialog is open, so a closed dialog
				    doesn't eagerly load every wizard user on the members page. */}
				<Suspense
					fallback={
						<div className='flex justify-center py-2'>
							<CircularProgress size={24} />
						</div>
					}
				>
					{open && <AddMemberUserSelect value={selectedUserId} onChange={setSelectedUserId} />}
				</Suspense>

				<FormControl fullWidth>
					<Select value={selectedRole} onChange={(e) => setSelectedRole(e.target.value as Auth.MembershipRole)}>
						<MenuItem value={Auth.MembershipRole.Admin}>Admin</MenuItem>
						<MenuItem value={Auth.MembershipRole.Member}>Member</MenuItem>
						<MenuItem value={Auth.MembershipRole.Trainee}>Trainee</MenuItem>
					</Select>
				</FormControl>
			</DialogContent>
			<DialogActions>
				<Button onClick={onClose}>Cancel</Button>
				<Button onClick={handleSubmit} loading={isPending} disabled={!selectedUserId}>
					Add
				</Button>
			</DialogActions>
		</Dialog>
	);
}

/**
 * User picker for the add-member dialog. Isolated so the all-users fetch it
 * triggers only runs while the dialog is open.
 *
 * @param props - Component props.
 * @param props.value - Currently selected user id.
 * @param props.onChange - Called with the chosen user id.
 */
function AddMemberUserSelect({ value, onChange }: { value: string; onChange: (userId: string) => void }) {
	const { data: usersData } = useAllWizardUsers();

	return (
		<FormControl fullWidth>
			<Select
				value={value}
				onChange={(e) => onChange(e.target.value)}
				displayEmpty
				renderValue={(selected) => {
					if (!selected) {
						return <span className='text-gray-a11 italic'>Select user</span>;
					}
					const user = usersData.users.find((u) => u.id === selected);
					return user?.displayName ?? user?.username ?? selected;
				}}
			>
				{usersData.users.map((user) => (
					<MenuItem key={user.id} value={user.id}>
						{user.displayName ?? user.username}
					</MenuItem>
				))}
			</Select>
		</FormControl>
	);
}
